/**
 * Game-side bridge client (ADR-001).
 *
 * Runs INSIDE the game iframe. On `init()` it posts a `handshake` to
 * `window.parent` and waits for a `handshake_ack` from the configured host
 * origin within a short timeout. If the ack arrives the bridge is "active"
 * and full-screen show* calls are proxied to the host via postMessage. If the
 * ack never arrives (no host shell — dev / direct load / unexpected embed —
 * not expected in production since games live only on getjar.com), the bridge
 * stays inactive and `send()` for `request_show` resolves with
 * `{ outcome: 'no_ad', reason: 'no_host' }`. The bridge NEVER renders an ad
 * itself.
 *
 * Origin trust is strict and one-way: the bridge only accepts messages
 * whose `event.origin === config.hostOrigin`. The host-side controller does
 * the symmetric check.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { buildMessage, newCorrelationId, parseMessage, } from './protocol';
/** Default handshake timeout. Short — production hosts ack on the next event-loop tick. */
const DEFAULT_HANDSHAKE_TIMEOUT_MS = 1000;
export class BridgeClient {
    constructor(config) {
        var _a, _b, _c, _d;
        this.active = false;
        this.features = {};
        this.pending = new Map();
        this.listenerAttached = false;
        this.hostOrigin = config.hostOrigin;
        this.parentWindow = (_a = config.parentWindow) !== null && _a !== void 0 ? _a : (typeof window !== 'undefined' ? window.parent : null);
        this.ownWindow = (_b = config.ownWindow) !== null && _b !== void 0 ? _b : (typeof window !== 'undefined' ? window : null);
        this.handshakeTimeoutMs = (_c = config.handshakeTimeoutMs) !== null && _c !== void 0 ? _c : DEFAULT_HANDSHAKE_TIMEOUT_MS;
        this.sdkVersion = (_d = config.sdkVersion) !== null && _d !== void 0 ? _d : '1.0.0';
        this.listener = (event) => this.handleMessage(event);
    }
    /**
     * Initiate the handshake. Resolves true if a `handshake_ack` arrives from
     * the configured host origin within the timeout; false otherwise (no host
     * shell present). Idempotent: calling twice does not re-handshake.
     */
    init(appId, publisher) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.active)
                return true;
            if (!this.ownWindow || !this.parentWindow)
                return false;
            this.attachListener();
            const correlationId = newCorrelationId();
            const ackReceived = new Promise((resolve) => {
                const timer = this.ownWindow.setTimeout(() => {
                    // Timeout: drop the pending entry and signal no-host. Never throws.
                    this.pending.delete(correlationId);
                    resolve(false);
                }, this.handshakeTimeoutMs);
                this.pending.set(correlationId, {
                    resolve: (payload) => {
                        var _a;
                        this.ownWindow.clearTimeout(timer);
                        const ack = (payload !== null && payload !== void 0 ? payload : {});
                        this.features = (_a = ack.features) !== null && _a !== void 0 ? _a : {};
                        this.active = true;
                        resolve(true);
                    },
                    // Reject path is unused for the handshake; we never call it.
                    reject: () => {
                        this.ownWindow.clearTimeout(timer);
                        resolve(false);
                    },
                });
            });
            const payload = {
                sdkVersion: this.sdkVersion,
                appId,
                publisher,
            };
            this.parentWindow.postMessage(buildMessage('handshake', correlationId, payload), this.hostOrigin);
            return ackReceived;
        });
    }
    /**
     * Send a `request_show` to the host. Resolves with the host's `ad_result`,
     * or — if the bridge never handshook — a synthetic
     * `{ outcome: 'no_ad', reason: 'no_host' }`. Never throws.
     */
    requestShow(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.active) {
                return { outcome: 'no_ad', reason: 'no_host' };
            }
            const result = (yield this.dispatch('request_show', payload));
            return result !== null && result !== void 0 ? result : { outcome: 'error', reason: 'malformed_result' };
        });
    }
    /**
     * Ask the host if a format is ready (preloaded). When the bridge is
     * inactive resolves `ready: false` — UI gates that depend on this stay
     * dark in the no-host edge case.
     */
    queryReady(payload) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.active) {
                return { format: payload.format, ready: false };
            }
            const result = (yield this.dispatch('query_ready', payload));
            return result !== null && result !== void 0 ? result : { format: payload.format, ready: false };
        });
    }
    isActive() {
        return this.active;
    }
    /** Snapshot of host-advertised features. Empty until handshake completes. */
    getFeatures() {
        return Object.assign({}, this.features);
    }
    /** Detach the message listener and clear pending state. Idempotent. */
    destroy() {
        if (this.ownWindow && this.listenerAttached) {
            this.ownWindow.removeEventListener('message', this.listener);
            this.listenerAttached = false;
        }
        for (const pending of this.pending.values()) {
            pending.reject(new Error('bridge destroyed'));
        }
        this.pending.clear();
        this.active = false;
    }
    // --- internals -----------------------------------------------------------
    attachListener() {
        if (this.listenerAttached || !this.ownWindow)
            return;
        this.ownWindow.addEventListener('message', this.listener);
        this.listenerAttached = true;
    }
    dispatch(type, payload) {
        const correlationId = newCorrelationId();
        const promise = new Promise((resolve, reject) => {
            this.pending.set(correlationId, { resolve, reject });
        });
        this.parentWindow.postMessage(buildMessage(type, correlationId, payload), this.hostOrigin);
        return promise;
    }
    handleMessage(event) {
        // Trust only the configured host origin. This is the load-bearing security
        // check — a misbehaving frame on a different origin can postMessage to us
        // but we drop everything that doesn't match.
        if (event.origin !== this.hostOrigin)
            return;
        const parsed = parseMessage(event.data);
        if (!parsed.ok)
            return;
        const { message } = parsed;
        const pending = this.pending.get(message.correlationId);
        if (!pending)
            return; // unsolicited; ignore
        this.pending.delete(message.correlationId);
        pending.resolve(message.payload);
    }
}
